import { Button, Label, Modal, RangeSlider, Switch } from "@xola/ui-kit";
import clsx from "clsx";
import dayjs from "dayjs";
import { clamp } from "lodash";
import pluralize from "pluralize";
import React, { useState } from "react";
import { Channel, ChannelType } from "types";
import "./AbandonedBookingReminderPreference.css";
import { NotificationPreferenceListItem } from "./useNotificationPreferenceList";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { QuantitySpinner } from "@/components/Common/QuantitySpinner/QuantitySpinner";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { TimeslotTimePicker } from "@/components/PurchaseModification/TimeslotTimePicker";
import { useModal } from "@/hooks/useVisibility";

// Slider positions encode two adjacent ranges on a single integer axis:
//   positions 0..24 → 0–24 hours
//   positions 25..31 → 1–7 days (position - HOURS_MAX = day count)
// The vertical divider sits in the gap between 24 hours and 1 day.
const HOURS_MAX = 24;
const DAYS_MAX = 7;
const SLIDER_MIN = 0;
const SLIDER_MAX = HOURS_MAX + DAYS_MAX;
const DIVIDER_PERCENT = `${((HOURS_MAX + 0.5) / SLIDER_MAX) * 100}%`;
const MAX_REMINDERS = 3;
const DEFAULT_VALUES = [8, 12, 27]; // 8h, 12h, 3d
const ANY_TIME_START = 0;
const ANY_TIME_END = 2400;
const MIN_GAP_HOURS = 4;

const TOOLTIP_FORMATTER = {
    from: (value: number) => value,
    to: (value: number) => humanizeSliderValue(value),
};

interface ApplyPayload {
    channels: Channel[];
    sendStartTime: number;
    sendEndTime: number;
}

interface ModalProps {
    item: NotificationPreferenceListItem;
    onApply: (update: ApplyPayload) => void;
}

const getEmailChannels = (item: NotificationPreferenceListItem) =>
    item.channels.filter((channel) => channel.type === ChannelType.EMAIL);

export const AbandonedBookingReminderPreferenceModal = ({ item, onApply }: ModalProps) => {
    const modal = useModal();
    const emailChannels = getEmailChannels(item);

    const handleApply = (payload: ApplyPayload) => {
        onApply(payload);
        modal.hide();
    };

    return (
        <>
            Sent
            <span className="mx-1 cursor-pointer border-b border-primary font-bold text-blue" onClick={modal.show}>
                {emailChannels.length > 0 ? formatOffsetList(emailChannels) : "—"}
            </span>
            after booking is abandoned
            <Modal isOpen={modal.isVisible} onClose={modal.hide}>
                <ModalContent
                    initialChannels={emailChannels}
                    initialStart={item.sendStartTime ?? ANY_TIME_START}
                    initialEnd={item.sendEndTime ?? ANY_TIME_END}
                    onApply={handleApply}
                    onClose={modal.hide}
                />
            </Modal>
        </>
    );
};

const formatOffsetList = (channels: Channel[]) =>
    channels.map((channel, index) => (
        <React.Fragment key={`${channel.type}-${channel.offset}`}>
            {index > 0 && ", "}
            <NoTranslate>{humanizeIsoDuration(channel.offset)}</NoTranslate>
        </React.Fragment>
    ));

const humanizeSliderValue = (value: number) => {
    const position = clamp(Math.round(value), SLIDER_MIN, SLIDER_MAX);
    if (position <= HOURS_MAX) {
        return `${position} ${pluralize("hour", position)}`;
    }

    const days = position - HOURS_MAX;
    return `${days} ${pluralize("day", days)}`;
};

const humanizeIsoDuration = (iso: string) => humanizeSliderValue(isoToSliderValue(iso));

const sliderValueToIso = (value: number) => {
    const position = clamp(Math.round(value), SLIDER_MIN, SLIDER_MAX);
    if (position <= HOURS_MAX) {
        return `PT${position}H`;
    }

    return `P${position - HOURS_MAX}D`;
};

// The slider has a non-uniform axis (positions 0-24 cover 0-24 hours, 25-31 cover 1-7
// days), so to enforce a minimum spacing in real time we have to convert to hours first.
const sliderValueToHours = (value: number) => {
    const position = clamp(Math.round(value), SLIDER_MIN, SLIDER_MAX);
    return position <= HOURS_MAX ? position : (position - HOURS_MAX) * 24;
};

// `values` is maintained in sorted order by deriveSeedValues / handleCountChange /
// clampToMinGap, so we can compare adjacent entries without re-sorting.
const hasMinGap = (values: number[]) => {
    for (let index = 1; index < values.length; index += 1) {
        if (sliderValueToHours(values[index]) - sliderValueToHours(values[index - 1]) < MIN_GAP_HOURS) {
            return false;
        }
    }
    return true;
};

// Map an hour count back onto a slider position. `roundUp` picks the smallest valid
// position whose hour-equivalent is ≥ the input (used when clamping a handle up to
// satisfy a left-neighbor gap); `roundUp=false` picks the largest position ≤ the input
// (used when clamping down). `preferDays` keeps the result in the days region when the
// dragged handle started there, so a 24-hour clamp lands on `1 day` rather than `24 hours`.
const hoursToSliderPosition = (hours: number, options: { roundUp: boolean; preferDays: boolean }) => {
    const safe = clamp(hours, 0, DAYS_MAX * HOURS_MAX);
    if (safe <= HOURS_MAX && !options.preferDays) {
        return options.roundUp ? Math.ceil(safe) : Math.floor(safe);
    }
    const days = options.roundUp ? Math.ceil(safe / HOURS_MAX) : Math.floor(safe / HOURS_MAX);
    return HOURS_MAX + clamp(days, 1, DAYS_MAX);
};

const clampToMinGap = (candidate: number[], previous: number[]): number[] => {
    if (candidate.length !== previous.length) {
        return candidate;
    }
    const movedIndex = candidate.findIndex((value, index) => value !== previous[index]);
    if (movedIndex === -1) {
        return candidate;
    }

    const movedHours = sliderValueToHours(candidate[movedIndex]);
    const leftHours = movedIndex > 0 ? sliderValueToHours(previous[movedIndex - 1]) : Number.NEGATIVE_INFINITY;
    const rightHours =
        movedIndex < previous.length - 1 ? sliderValueToHours(previous[movedIndex + 1]) : Number.POSITIVE_INFINITY;

    const minHours = leftHours + MIN_GAP_HOURS;
    const maxHours = rightHours - MIN_GAP_HOURS;

    if (movedHours >= minHours && movedHours <= maxHours) {
        return candidate;
    }

    const clampUp = movedHours < minHours;
    const targetHours = clampUp ? minHours : maxHours;
    const preferDays = candidate[movedIndex] > HOURS_MAX;
    const newPosition = hoursToSliderPosition(targetHours, { roundUp: clampUp, preferDays });

    const result = [...previous];
    result[movedIndex] = clamp(newPosition, SLIDER_MIN, SLIDER_MAX);
    return result;
};

const isoToSliderValue = (iso: string) => {
    // Distinguish `P{n}D` (days region) from `PT{n}H` so saving/loading preserves unit:
    // e.g. "PT24H" must map back to 24 hours, not "1 day".
    const normalizedIso = iso?.trim() || "PT0M";
    const dayMatch = /^p(\d+)d$/i.exec(normalizedIso);
    if (dayMatch) {
        const days = Number(dayMatch[1]);
        if (days >= 1 && days <= DAYS_MAX) {
            return HOURS_MAX + days;
        }
    }

    const duration = dayjs.duration(normalizedIso);
    return clamp(Math.round(duration.asHours()), SLIDER_MIN, HOURS_MAX);
};

interface ModalContentProps {
    initialChannels: Channel[];
    initialStart: number;
    initialEnd: number;
    onApply: (payload: ApplyPayload) => void;
    onClose: () => void;
}

const ModalContent = ({ initialChannels, initialStart, initialEnd, onApply, onClose }: ModalContentProps) => {
    const [values, setValues] = useState<number[]>(() => deriveSeedValues(initialChannels));
    const [sendStart, setSendStart] = useState(initialStart);
    const [sendEnd, setSendEnd] = useState(initialEnd);
    // Bumped whenever an invalid drag clamps the moved handle back to its current position.
    // The underlying noUiSlider memoizes on deep-equal `start` arrays, so we have to remount
    // it to force the visual to snap back when our React state didn't actually change.
    const [sliderRevision, setSliderRevision] = useState(0);

    const isAnyTime = sendStart === ANY_TIME_START && sendEnd === ANY_TIME_END;
    const tooltipCustomFormatter = values.map(() => TOOLTIP_FORMATTER);
    const isReminderGapValid = hasMinGap(values);

    const handleCountChange = (count: number) => {
        const next = [...values];
        if (count < values.length) {
            next.pop();
        } else if (count > values.length) {
            next.push(pickNextReminder(next));
        }

        next.sort((previous, current) => previous - current);
        setValues(next);
    };

    const handleSliderChange = (next: string[]) => {
        const candidate = next.map((raw) => clamp(Math.round(Number(raw)), SLIDER_MIN, SLIDER_MAX));
        const clamped = clampToMinGap(candidate, values);
        const isUnchanged = clamped.every((value, index) => value === values[index]);
        if (isUnchanged) {
            setSliderRevision((revision) => revision + 1);
            return;
        }
        setValues(clamped);
    };

    const handleSelectAnyTime = () => {
        setSendStart(ANY_TIME_START);
        setSendEnd(ANY_TIME_END);
    };

    const handleSelectTimeRange = () => {
        setSendStart(ANY_TIME_START);
        setSendEnd(ANY_TIME_START);
    };

    const handleApply = () => {
        // Preserve the row-level on/off state — editing reminder timing must not silently re-enable
        // a notification the user had turned off via the row toggle.
        const isEnabled = initialChannels[0]?.enabled ?? true;
        const channels: Channel[] = values.map((value) => ({
            type: ChannelType.EMAIL,
            enabled: isEnabled,
            negativeOffset: false,
            offset: sliderValueToIso(value),
        }));
        onApply({ channels, sendStartTime: sendStart, sendEndTime: sendEnd });
    };

    return (
        <>
            <Modal.Header>Abandoned Booking Reminder</Modal.Header>

            <Modal.Body className="flex-col">
                <div className="flex flex-row items-center">
                    <QuantitySpinner
                        classNames={{ fieldset: "mr-2" }}
                        value={values.length}
                        min={1}
                        max={MAX_REMINDERS}
                        onChange={handleCountChange}
                    />
                    <div className="font-bold">Number of reminders</div>
                </div>

                <div className="mt-4">
                    <Label>Duration after booking abandonment</Label>
                </div>
                <div
                    className={clsx(
                        "abandoned-reminder-slider relative mb-12 flex flex-row",
                        values.length > 1 ? "mt-12" : "mt-2",
                    )}
                >
                    <RangeSlider
                        key={`${values.length}-${sliderRevision}`}
                        values={values}
                        min={SLIDER_MIN}
                        max={SLIDER_MAX}
                        tooltipCustomFormatter={tooltipCustomFormatter}
                        onChange={handleSliderChange}
                    />
                    <hr
                        className="absolute -mt-1 h-4 border-l-2 border-solid border-black"
                        style={{ left: DIVIDER_PERCENT }}
                    />
                </div>

                {!isReminderGapValid && (
                    <div className="-mt-8 mb-4 text-sm text-danger">
                        Reminders must be at least {MIN_GAP_HOURS} hours apart.
                    </div>
                )}

                <div className="mt-4 space-y-1">
                    <Label>Send Window</Label>
                    <label className="flex items-center gap-1">
                        <input type="radio" checked={isAnyTime} onChange={handleSelectAnyTime} />
                        <span>Any Time</span>
                    </label>
                    <label className="flex items-center gap-2 whitespace-nowrap">
                        <input type="radio" checked={!isAnyTime} onChange={handleSelectTimeRange} />
                        <span>Within time range</span>
                        {!isAnyTime && (
                            <>
                                <TimeslotTimePicker value={sendStart} onChange={setSendStart} />
                                <span>to</span>
                                <TimeslotTimePicker value={sendEnd} onChange={setSendEnd} />
                            </>
                        )}
                    </label>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <Button disabled={!isReminderGapValid} onClick={handleApply}>
                    Save
                </Button>
            </Modal.Footer>
        </>
    );
};

const deriveSeedValues = (channels: Channel[]): number[] => {
    if (channels.length === 0) {
        return [DEFAULT_VALUES[0]];
    }

    return channels.map((channel) => isoToSliderValue(channel.offset)).sort((previous, current) => previous - current);
};

// Picks the smallest valid slider position whose hour-equivalent is at least
// MIN_GAP_HOURS past the largest existing reminder. Falls into the days region when
// the target overflows the hours range so we don't pick a position that re-collides
// with hour positions (e.g. position 25 = 1 day = 24 hours).
const pickNextReminder = (current: number[]): number => {
    const maxHours = Math.max(...current.map((value) => sliderValueToHours(value)));
    const targetHours = maxHours + MIN_GAP_HOURS;

    const candidate = targetHours <= HOURS_MAX ? targetHours : HOURS_MAX + Math.ceil(targetHours / 24);

    return clamp(candidate, SLIDER_MIN, SLIDER_MAX);
};

interface ChannelsProps {
    item: NotificationPreferenceListItem;
    onApply: (channels: Channel[]) => void;
}

export const AbandonedBookingReminderChannelsPreference = ({ item, onApply }: ChannelsProps) => {
    // Toggle reflects channel state only; the master abandoned.enabled flag is owned by
    // the ABR page's enable switch, so gating on it here would show the toggle unchecked
    // even after the user flipped it on.
    const allEnabled = getEmailChannels(item).every((channel) => channel.enabled);

    const handleToggle = (isChecked: boolean) => {
        onApply(item.channels.map((channel) => ({ ...channel, enabled: isChecked })));
    };

    return (
        <Switch.Group>
            <Switch isChecked={allEnabled} size="medium" onChange={handleToggle} />
            <Switch.Label direction="left">
                <span className="pl-2 text-sm text-gray-darker">Email</span>
            </Switch.Label>
        </Switch.Group>
    );
};
